import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import {Paper} from "@mui/material";
import {Col} from "reactstrap";
import Form from "../../../../../../../components/base/form";
import {OutlinedInputForm} from "../../../../../../../components/base/input/outlined_input";
import Button from "../../../../../../../components/base/button/button";
import TryAgain from "../../../../../../../components/app-specific/try-again";
import WarrantyAndReturnTemplateCard from "../../../../../../../components/app-specific/part-support/part-support-setting-cards/template-card";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import useQuery from "../../../../../../../hooks/use-query";


const schema = Yup.object().shape({
    newTemplate: Yup.string().trim().nullable().required(ValidateMessages.required)
})

const subscription = {submitting: true, values: true, initialValues: true};

const WarrantyAndReturnProcessSettingsTemplates = ({fileTypes, loading, templates: templatesProps, section}) => {
    const [query] = useQuery()
    const [creating, setCreating] = useState(false)
    const [templates, setTemplates] = useState([])
    const validate = makeValidate(schema)
    const required = makeRequired(schema)
    const isMounted = useIsMounted()

    /**
     * Listens for the changes in templateProps and with each change:
     * replaces the templateProps with the state list.
     */
    useEffect(() => {
        setTemplates(templatesProps)
    }, [templatesProps])

    /**
     * Create a new template with the given values.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createTemplate = async (values, form) => {
        setCreating(true)
        const forApi = {
            categoryId: Number(query.categoryId),
            partSupportRequestTypeId: Number(query.typeId),
            title: values.newTemplate,
            isRequired: false,
        }
        const response = await Api.createPartSupportRequestFileTemplate(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            const newTemplate = {
                ...response.data,
                editing: false,
            }
            setTemplates(prevState => [
                newTemplate,
                ...prevState
            ])
            setTimeout(() => form.restart({}), 0)
        }
        setCreating(false)
        return false
    }

    /**
     * Updates a template with the given values.
     * @param {any} template the form values
     * @return {Promise<boolean>}
     */
    const updateTemplate = async (template) => {
        setTemplates(prevState => prevState.map(e => e.id === template.id ? {
            ...e,
            editing: true
        } : e))
        const forApi = {
            ...template,
            validFileTypeId: template.validFileType?.id,
        }
        const response = await Api.editPartSupportRequestFileTemplate(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setTemplates(prevState => prevState?.map(e => e.id === template.id
                ? {...e, ...template}
                : e))
        }
        setTemplates(prevState => prevState?.map(e => e.id === template.id
            ? {...e, editing: false}
            : e))
        return response?.resultFlag ?? false
    }

    /**
     * Removes a file template from the server and then removes it from the local list.
     * @param {any} template the template to be removed
     */
    const removeTemplate = (template) => {
        setTemplates(prevState => prevState.map(e => e.id === template.id ? {
            ...e,
            editing: true
        } : e))
        Api.removePartSupportRequestFileTemplate(template.id).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setTemplates(prevState => prevState?.filter(e => e.id !== template.id))
            } else {
                setTemplates(prevState => prevState?.map(e => e.id === template.id
                    ? {...e, editing: false}
                    : e))
            }
        })
    }

    return (
        <>
            <Paper
                id={section.id}
                className='paper w-100 px-3 py-2 flex-wrap justify-content-between align-items-start'>
                <Col xs={12}>
                    <p className={'text-lg font-weight-bold mt-4 mb-4'}>
                        {section.name}
                    </p>
                </Col>
                <Form
                    className='d-flex flex-wrap w-100 align-items-center'
                    onSubmit={createTemplate}
                    subscription={subscription}
                    validate={validate}
                    initialValues={{}}
                    render={({values, form}) => {
                        return (
                            <>
                                <Col className={'pl-3'}>
                                    <OutlinedInputForm
                                        name={'newTemplate'}
                                        placeholder={'title of the new Template'}
                                        label={"New Template"}
                                        required={required.newTemplate}
                                        value={values.newTemplate ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                    />
                                </Col>
                                <Col xs={'auto'}>
                                    <Button
                                        className={'px-5'}
                                        type={'submit'}
                                        disabled={creating}
                                        color={'primary'}
                                        variant={'contained'}
                                    >
                                        {
                                            creating
                                                ? "Submitting..."
                                                : "Submit"
                                        }
                                    </Button>
                                </Col>
                            </>
                        )
                    }}
                />
                <Col xs={12} className={'mt-3 d-flex flex-wrap'}>
                    {
                        loading
                            ? Array(5).fill(null).map((e, i) => (
                                <div key={i} className={'loading normal w-100 mb-3'}>
                                    <div/>
                                </div>
                            ))
                            : templates?.length < 1
                                ? <TryAgain
                                    text={"There is currently no templates submitted. you can add one" +
                                        " using the form above."}
                                />
                                : <TransitionGroup className={'d-flex flex-wrap w-100'}>
                                    {
                                        templates?.map(template => (
                                            <CSSTransition
                                                key={template.id}
                                                classNames='item'
                                                timeout={500}
                                            >
                                                <WarrantyAndReturnTemplateCard
                                                    template={template}
                                                    fileTypes={fileTypes}
                                                    update={updateTemplate}
                                                    remove={removeTemplate}
                                                />
                                            </CSSTransition>
                                        ))
                                    }
                                </TransitionGroup>
                    }
                </Col>
            </Paper>
        </>
    );
}

export default WarrantyAndReturnProcessSettingsTemplates;
